import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getEmployees, getCredentials, getHospitals, updateEmployee, createEmployee } from '../services/api.ts';
import LockToggle from './LockToggle.tsx';

// ---------------------------------------------------------------------------
// Credentials tab.
//
// Two matrices — Anesthesiologists and CRNAs — of EVERY provider the group owns (not just today's
// workers). Names run down the left, alphabetical by last name (sticky). The sites the group covers run
// across the top (sticky, scrolls left/right). A green check sits where a provider is credentialed at
// that site.
//
// SITE COLUMNS = the credential catalog + EVERY hospital name + any credential already on a card. So
// when the group adds a hospital, it automatically shows up here as a credential site too.
//
// EDITING: the page starts LOCKED (read-only). Click "Locked" to unlock; then every cell is a clickable
// toggle — click an empty box to credential someone at that site, click a green check to remove it.
// Each change saves immediately to that person's card. A quick-add row lets you add a new anesthesiologist
// or CRNA right here. Auto-locks after 30 seconds idle (or lock manually).
// ---------------------------------------------------------------------------

interface EmployeeLike { employeeId: string; employeeType: string; firstName: string; lastName: string; credentials?: string[]; homeHospitalId?: string; [k: string]: any; }
interface HospitalLike { hospitalId: string; name: string; }

const AUTO_LOCK_MS = 30000;
const CHECK_GREEN = '#1f9d55';

const CredentialsView: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState<{ text: string; kind: 'info' | 'error' } | null>(null);
  const [employees, setEmployees] = useState<EmployeeLike[]>([]);
  const [catalog, setCatalog] = useState<string[]>([]);
  const [hospitals, setHospitals] = useState<HospitalLike[]>([]);
  const [filter, setFilter] = useState('');

  // Each list (Anesthesiologists / CRNAs) locks & unlocks on its own.
  const [lockedByType, setLockedByType] = useState<Record<'doctor' | 'nurse', boolean>>({ doctor: true, nurse: true });
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Snapshot of the last bulk change so it can be undone (restores each affected person's prior credentials).
  const [lastBulk, setLastBulk] = useState<{ site: string; add: boolean; groupLabel: string; type: 'doctor' | 'nurse'; snapshot: { employeeId: string; credentials: string[] }[] } | null>(null);
  const lockTimers = useRef<Record<'doctor' | 'nurse', ReturnType<typeof setTimeout> | null>>({ doctor: null, nurse: null });

  // Keep a live copy so rapid clicks read the latest credentials (not a stale render closure).
  const employeesRef = useRef<EmployeeLike[]>([]);
  useEffect(() => { employeesRef.current = employees; }, [employees]);

  // Quick-add (only one form open at a time).
  const [addingType, setAddingType] = useState<'doctor' | 'nurse' | null>(null);
  const [addFirst, setAddFirst] = useState('');
  const [addLast, setAddLast] = useState('');
  const [addHome, setAddHome] = useState('');
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const [emp, cred, hosp] = await Promise.all([
          getEmployees(),
          getCredentials().catch(() => ({ credentials: [] })),
          getHospitals().catch(() => ({ hospitals: [] })),
        ]);
        if (!cancelled) {
          setEmployees(emp.employees || []);
          setCatalog(cred.credentials || []);
          setHospitals(hosp.hospitals || []);
        }
      } catch (e) {
        if (!cancelled) { setError('Failed to load employees'); console.error(e); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => { (['doctor', 'nurse'] as const).forEach(t => { const tm = lockTimers.current[t]; if (tm) clearTimeout(tm); }); }, []);
  const bumpAutoLock = (type: 'doctor' | 'nurse') => {
    const existing = lockTimers.current[type];
    if (existing) clearTimeout(existing);
    lockTimers.current[type] = setTimeout(() => {
      setLockedByType(prev => ({ ...prev, [type]: true }));
      setAddingType(cur => cur === type ? null : cur);
    }, AUTO_LOCK_MS);
  };
  const unlock = (type: 'doctor' | 'nurse') => { setLockedByType(prev => ({ ...prev, [type]: false })); bumpAutoLock(type); };
  const lockNow = (type: 'doctor' | 'nurse') => {
    const tm = lockTimers.current[type];
    if (tm) clearTimeout(tm);
    setLockedByType(prev => ({ ...prev, [type]: true }));
    setAddingType(cur => cur === type ? null : cur);
  };

  // Site columns: credential catalog ∪ hospital names ∪ any credential already on a card.
  const sites = useMemo(() => {
    const s = new Set<string>();
    for (const c of catalog) if (c && c.trim()) s.add(c);
    for (const h of hospitals) if (h.name && h.name.trim()) s.add(h.name);
    for (const e of employees) for (const c of e.credentials || []) s.add(c);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [catalog, hospitals, employees]);

  const doctors = employees.filter(e => e.employeeType === 'doctor');
  const crnas = employees.filter(e => e.employeeType === 'nurse' || e.employeeType.startsWith('nurse-student'));

  const byLastName = (a: EmployeeLike, b: EmployeeLike) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
  const matchesFilter = (e: EmployeeLike) => {
    const q = filter.trim().toLowerCase();
    return !q || `${e.firstName} ${e.lastName}`.toLowerCase().includes(q);
  };

  // Toggle one credential on one person and save it to their card immediately.
  const toggleCredential = (empId: string, site: string, type: 'doctor' | 'nurse') => {
    if (lockedByType[type]) return;
    const key = `${empId}|${site}`;
    if (saving.has(key)) return;
    bumpAutoLock(type);
    const current = employeesRef.current.find(e => e.employeeId === empId);
    if (!current) return;
    const held = new Set(current.credentials || []);
    if (held.has(site)) held.delete(site); else held.add(site);
    const updated: EmployeeLike = { ...current, credentials: [...held] };

    // Optimistic update (and keep the ref fresh for rapid successive clicks).
    setEmployees(list => list.map(x => x.employeeId === empId ? updated : x));
    employeesRef.current = employeesRef.current.map(x => x.employeeId === empId ? updated : x);
    setSaving(s => new Set(s).add(key));

    const { employeeId, ...rest } = updated;
    updateEmployee(empId, rest as any)
      .then(() => setNote(null))
      .catch(err => {
        console.error(err);
        // Revert on failure.
        setEmployees(list => list.map(x => x.employeeId === empId ? current : x));
        employeesRef.current = employeesRef.current.map(x => x.employeeId === empId ? current : x);
        setNote({ text: 'Could not save that change — please try again.', kind: 'error' });
      })
      .finally(() => setSaving(s => { const n = new Set(s); n.delete(key); return n; }));
  };

  // Run async work over a list in small parallel chunks (so a bulk change doesn't fire hundreds of
  // requests at once).
  const runChunked = async <T,>(items: T[], size: number, fn: (item: T) => Promise<void>) => {
    for (let i = 0; i < items.length; i += size) {
      await Promise.all(items.slice(i, i + size).map(fn));
    }
  };

  // Credential (or un-credential) EVERY person in a matrix at one site in a single action — hospitals
  // often credential the entire group at once. Confirms first, updates optimistically, saves each card.
  const bulkSetSite = async (people: EmployeeLike[], site: string, add: boolean, groupLabel: string, type: 'doctor' | 'nurse') => {
    if (lockedByType[type] || bulkBusy) return;
    const targets = people.filter(p => (add ? !(p.credentials || []).includes(site) : (p.credentials || []).includes(site)));
    if (targets.length === 0) return;
    const verb = add ? 'Credential' : 'Remove the credential for';
    if (!window.confirm(`${verb} all ${people.length} ${groupLabel} at "${site}"?`)) return;
    bumpAutoLock(type);
    // Snapshot the affected people's CURRENT credentials so the whole change can be undone.
    const snapshot = targets.map(t => ({ employeeId: t.employeeId, credentials: [...(t.credentials || [])] }));
    const targetIds = new Set(targets.map(t => t.employeeId));
    const apply = (x: EmployeeLike): EmployeeLike => {
      if (!targetIds.has(x.employeeId)) return x;
      const held = new Set(x.credentials || []);
      if (add) held.add(site); else held.delete(site);
      return { ...x, credentials: [...held] };
    };
    // Optimistic (and keep the ref fresh so per-card saves send the new value).
    setEmployees(list => list.map(apply));
    employeesRef.current = employeesRef.current.map(apply);
    setBulkBusy(true);
    setNote(null);
    const toSave = employeesRef.current.filter(x => targetIds.has(x.employeeId));
    let failures = 0;
    await runChunked(toSave, 12, async (emp) => {
      const { employeeId, ...rest } = emp;
      try { await updateEmployee(employeeId, rest as any); } catch (e) { console.error(e); failures++; }
    });
    setBulkBusy(false);
    setLastBulk({ site, add, groupLabel, type, snapshot }); // enable Undo
    if (failures) {
      // Re-sync from the server so the grid reflects what actually saved.
      try { const fresh = await getEmployees(); setEmployees(fresh.employees || []); } catch { /* ignore */ }
      setNote({ text: `${failures} of ${toSave.length} didn't save — the page has been refreshed to show what stuck.`, kind: 'error' });
    } else {
      setNote({ text: `${add ? 'Credentialed' : 'Removed'} all ${targets.length} ${groupLabel} ${add ? 'at' : 'from'} "${site}".`, kind: 'info' });
    }
  };

  // Undo the last bulk change — restore each affected person's credentials to the saved snapshot.
  const undoBulk = async () => {
    if (!lastBulk || bulkBusy) return;
    if (!window.confirm(`Undo the last change and restore all ${lastBulk.snapshot.length} ${lastBulk.groupLabel} to how "${lastBulk.site}" was before?`)) return;
    bumpAutoLock(lastBulk.type);
    const byId = new Map(lastBulk.snapshot.map(s => [s.employeeId, s.credentials]));
    const apply = (x: EmployeeLike): EmployeeLike => byId.has(x.employeeId) ? { ...x, credentials: [...byId.get(x.employeeId)!] } : x;
    setEmployees(list => list.map(apply));
    employeesRef.current = employeesRef.current.map(apply);
    setBulkBusy(true);
    setNote(null);
    const toSave = employeesRef.current.filter(x => byId.has(x.employeeId));
    let failures = 0;
    await runChunked(toSave, 12, async (emp) => {
      const { employeeId, ...rest } = emp;
      try { await updateEmployee(employeeId, rest as any); } catch (e) { console.error(e); failures++; }
    });
    setBulkBusy(false);
    if (failures) {
      try { const fresh = await getEmployees(); setEmployees(fresh.employees || []); } catch { /* ignore */ }
      setNote({ text: `Undo partly failed (${failures}) — the page has been refreshed to show the current state.`, kind: 'error' });
    } else {
      setNote({ text: `Reverted — restored ${toSave.length} ${lastBulk.groupLabel} to how "${lastBulk.site}" was before.`, kind: 'info' });
    }
    setLastBulk(null);
  };

  const openAdd = (type: 'doctor' | 'nurse') => { setAddingType(type); setAddFirst(''); setAddLast(''); setAddHome(''); bumpAutoLock(type); };

  const submitAdd = async () => {
    const first = addFirst.trim(), last = addLast.trim();
    if (!first || !last) { setNote({ text: 'Enter a first and last name.', kind: 'error' }); return; }
    if (!addHome) { setNote({ text: 'Pick a home site for the new person.', kind: 'error' }); return; }
    if (addingType) bumpAutoLock(addingType);
    setAddBusy(true);
    try {
      const payload: any = { employeeType: addingType, firstName: first, lastName: last, homeHospitalId: addHome, credentials: [] };
      if (addingType === 'doctor') { payload.maxConcurrentNurseCoverageRatio = 4; payload.maxConcurrentStudentCoverageRatio = 2; }
      const res: any = await createEmployee(payload);
      const created: EmployeeLike = res.employee || res;
      setEmployees(prev => [...prev, created]);
      employeesRef.current = [...employeesRef.current, created];
      setAddingType(null); setAddFirst(''); setAddLast(''); setAddHome('');
      setNote({ text: `Added ${first} ${last}. Click their boxes to set where they're credentialed.`, kind: 'info' });
    } catch (e) {
      console.error(e);
      setNote({ text: 'Could not add that person — please try again.', kind: 'error' });
    } finally {
      setAddBusy(false);
    }
  };

  const NAME_W = 150; // sticky name column width (smaller so more site columns fit)
  // Sticky cells MUST be opaque or the scrolling table shows through them. Layer the tint over the
  // opaque surface token (same trick the Board's room-cell uses).
  const HEAD_BG = 'linear-gradient(var(--tint-header), var(--tint-header)), var(--bg-surface)';
  const nameBgFor = (zebra: boolean) => zebra ? 'linear-gradient(var(--tint-zebra), var(--tint-zebra)), var(--bg-surface)' : 'var(--bg-surface)';

  const renderCell = (e: EmployeeLike, site: string, zebra: boolean, type: 'doctor' | 'nurse', matrixLocked: boolean) => {
    const ok = (e.credentials || []).includes(site);
    const key = `${e.employeeId}|${site}`;
    const isSaving = saving.has(key);
    const bg = zebra ? 'var(--tint-zebra)' : 'transparent';
    if (matrixLocked) {
      return (
        <td key={site} style={{ background: bg, borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', padding: '2px 3px', textAlign: 'center' }}>
          {ok && (
            <span title={`Credentialed at ${site}`} style={{ display: 'inline-block', minWidth: '30px', padding: '1px 4px', borderRadius: '4px', background: CHECK_GREEN, color: '#fff', fontWeight: 800, fontSize: '0.82em' }}>✓</span>
          )}
        </td>
      );
    }
    return (
      <td key={site} style={{ background: bg, borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', padding: '2px 3px', textAlign: 'center' }}>
        <button
          type="button"
          disabled={isSaving}
          onClick={() => toggleCredential(e.employeeId, site, type)}
          title={ok ? `Click to remove credential at ${site}` : `Click to credential ${e.firstName} ${e.lastName} at ${site}`}
          style={{
            minWidth: '30px',
            padding: '1px 4px',
            borderRadius: '4px',
            fontWeight: 800,
            fontSize: '0.82em',
            cursor: isSaving ? 'default' : 'pointer',
            opacity: isSaving ? 0.5 : 1,
            color: ok ? '#fff' : 'var(--text-faint)',
            background: ok ? CHECK_GREEN : 'transparent',
            border: ok ? '1px solid rgba(0,0,0,0.15)' : '1px dashed var(--border-soft)',
            transition: 'background 0.1s',
          }}
        >
          {ok ? '✓' : '+'}
        </button>
      </td>
    );
  };

  const renderMatrix = (title: string, people: EmployeeLike[], accent: string, addType: 'doctor' | 'nurse', addLabel: string) => {
    const rows = people.filter(matchesFilter).sort(byLastName);
    const mlocked = lockedByType[addType];
    return (
      <div key={title} style={{ marginBottom: '22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>{title}</h2>
          <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{rows.length}{filter ? ` of ${people.length}` : ''}</span>
          <LockToggle locked={mlocked} onChange={(nextLocked) => (nextLocked ? lockNow(addType) : unlock(addType))} />
        </div>
        <div style={{ border: '1px solid var(--border-soft)', borderRadius: '8px', overflow: 'auto', maxHeight: '62vh' }}>
          {/* overflow:visible overrides the global `.App table { overflow: hidden }` (App.css) — otherwise the
              table becomes its OWN scroll container and the sticky header/name cells can never pin to the
              scrollable div above. Same reason `.App .board-table table` overrides it on the board. */}
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.74em', overflow: 'visible' }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 3, background: HEAD_BG, borderBottom: '2px solid ' + accent, borderRight: '1px solid var(--border-soft)', textAlign: 'left', padding: '5px 8px', minWidth: NAME_W, width: NAME_W }}>
                  Full Name
                </th>
                {sites.map(site => {
                  const have = people.reduce((n, p) => n + ((p.credentials || []).includes(site) ? 1 : 0), 0);
                  const allHave = people.length > 0 && have === people.length;
                  const someHave = have > 0 && have < people.length;
                  return (
                    <th key={site} title={site} style={{ position: 'sticky', top: 0, zIndex: 2, background: HEAD_BG, borderBottom: '2px solid ' + accent, borderRight: '1px solid var(--border-subtle)', padding: '4px 6px', whiteSpace: 'nowrap', fontSize: '0.9em', fontWeight: 700, color: 'var(--text-secondary)', minWidth: '56px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                        <span>{site}</span>
                        {!mlocked && (
                          <input
                            type="checkbox"
                            checked={allHave}
                            ref={el => { if (el) el.indeterminate = someHave; }}
                            disabled={bulkBusy}
                            onChange={() => bulkSetSite(people, site, !allHave, title.toLowerCase(), addType)}
                            title={`Credential ALL ${title.toLowerCase()} at ${site}`}
                            style={{ cursor: bulkBusy ? 'default' : 'pointer', width: '15px', height: '15px', margin: 0 }}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((e, i) => {
                const zebra = i % 2 === 1;
                return (
                  <tr key={e.employeeId}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: nameBgFor(zebra), borderRight: '1px solid var(--border-soft)', borderBottom: '1px solid var(--border-subtle)', padding: '3px 8px', fontWeight: 600, whiteSpace: 'nowrap', minWidth: NAME_W, width: NAME_W }}>
                      {e.lastName}, {e.firstName}
                      {e.employeeType.startsWith('nurse-student') && <span style={{ marginLeft: '6px', fontSize: '0.72em', color: 'var(--text-muted)', fontWeight: 700 }}>SRNA</span>}
                    </td>
                    {sites.map(site => renderCell(e, site, zebra, addType, mlocked))}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={sites.length + 1} style={{ padding: '10px', color: 'var(--text-faint)' }}>No matching {title.toLowerCase()}.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Quick-add (only when unlocked). */}
        {!mlocked && (
          <div style={{ marginTop: '8px' }}>
            {addingType === addType ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', padding: '8px 10px', border: '1px dashed var(--border-soft)', borderRadius: '8px', background: 'var(--bg-surface-raised)' }}>
                <input value={addFirst} onChange={e => setAddFirst(e.target.value)} placeholder="First name" className="form-control" style={{ maxWidth: '150px' }} />
                <input value={addLast} onChange={e => setAddLast(e.target.value)} placeholder="Last name" className="form-control" style={{ maxWidth: '150px' }} />
                <select value={addHome} onChange={e => setAddHome(e.target.value)} className="form-control" style={{ maxWidth: '200px' }}>
                  <option value="">Home site…</option>
                  {[...hospitals].sort((a, b) => a.name.localeCompare(b.name)).map(h => (
                    <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>
                  ))}
                </select>
                <button type="button" onClick={submitAdd} disabled={addBusy} style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid ' + accent, background: accent, color: '#fff', fontWeight: 600, cursor: addBusy ? 'default' : 'pointer', opacity: addBusy ? 0.6 : 1 }}>
                  {addBusy ? 'Adding…' : 'Add'}
                </button>
                <button type="button" onClick={() => setAddingType(null)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-soft)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => openAdd(addType)} style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-soft)', background: 'var(--bg-surface-raised)', color: 'var(--text-secondary)', fontSize: '0.85em', fontWeight: 600, cursor: 'pointer' }}>
                ＋ Add {addLabel}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '8px 4px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '26px' }}>Credentials</h1>
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search by name…" className="form-control" style={{ maxWidth: '220px' }} />
      </div>
      <div style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginBottom: '10px' }}>
        Every provider the group owns. Site columns are your credential list <strong>plus every hospital</strong> — add a hospital and it shows up here automatically.
        A green ✓ means they’re credentialed at that site. Use the <strong>toggle next to each list</strong> (Anesthesiologists / CRNAs) to unlock it — then click a box to add (＋ → ✓) or remove (✓) a credential, or use the checkbox under a site name to credential everyone in that list at once. Changes save right away.
      </div>

      {note && (
        <div style={{ marginBottom: '10px', fontSize: '0.85em', padding: '6px 10px', borderRadius: '6px', background: note.kind === 'error' ? 'rgba(220,38,38,0.10)' : 'rgba(31,157,85,0.10)', color: note.kind === 'error' ? 'var(--danger-text, #dc2626)' : 'var(--success-text, #157347)', border: '1px solid ' + (note.kind === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(31,157,85,0.3)') }}>
          {note.text}
        </div>
      )}
      {lastBulk && (
        <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '0.82em', padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-surface-raised)', border: '1px solid var(--border-soft)' }}>
          <span style={{ color: 'var(--text-muted)' }}>
            Last bulk change: {lastBulk.add ? 'credentialed' : 'removed'} all {lastBulk.groupLabel} {lastBulk.add ? 'at' : 'from'} “{lastBulk.site}”.
          </span>
          <button type="button" onClick={undoBulk} disabled={bulkBusy} style={{ padding: '3px 12px', borderRadius: '6px', border: '1px solid var(--amber)', background: 'var(--amber-soft, rgba(245,158,11,0.12))', color: 'var(--amber)', fontWeight: 700, cursor: bulkBusy ? 'default' : 'pointer' }}>
            ↺ Undo
          </button>
          <button type="button" onClick={() => setLastBulk(null)} style={{ padding: '3px 8px', border: 'none', background: 'none', color: 'var(--text-faint)', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}
      {error && <div style={{ color: 'var(--danger-text, #dc2626)', marginBottom: '10px' }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : sites.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>No site credentials or hospitals are recorded yet.</div>
      ) : (
        <>
          {renderMatrix('Anesthesiologists', doctors, 'var(--or-blue-mid)', 'doctor', 'anesthesiologist')}
          {renderMatrix('CRNAs', crnas, 'var(--amber)', 'nurse', 'CRNA')}
        </>
      )}
    </div>
  );
};

export default CredentialsView;
